//Built in Libraries

//Third Part Libraries
import { Bars3BottomLeftIcon } from '@heroicons/react/24/outline';
import { useEffect, useRef, useState } from 'react';
import { useIdleTimer } from 'react-idle-timer';
import { useSelector } from 'react-redux';
import { Outlet } from 'react-router';
//Components
import HeaderBar from '@/components/Nav/HeaderBar';
import SidebarNav from '@/components/Nav/SidebarNav';
//Assets
import Breadcrumbs from '@/components/UI/BreadCrumbs';
import Button from '@/components/UI/Button';
import Modal from '@/components/UI/Modal';
import Snackbar from '@/components/UI/Snackbar';
import { useAppDispatch } from '@/store';
import { toastSelector } from '../../store';
import { SignOutUser } from '../../store/Auth';

export default function AppLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const dispatch = useAppDispatch();
  const [showModal, setShowModal] = useState(false);
  const [_isTimedOut, setIsTimedOut] = useState(false);
  const [counter, setCounter] = useState(30);
  const appToast = useSelector(toastSelector);

  useEffect(() => {
    if (showModal && counter > 0) {
      const timeoutId = setInterval(() => setCounter((prevState) => prevState - 1), 1000);
      return () => clearInterval(timeoutId);
    }

    if (counter === 0) {
      dispatch(SignOutUser());
    }

    if (appToast.showToast) {
      snackbarRef.current?.closeToast();
    }
  }, [showModal, counter, appToast.showToast, dispatch]);

  const handleOnIdle = () => {
    setIsTimedOut(true);
    setShowModal(true);
  };

  const _handleOnActive = () => {
    reset();
    setCounter(30);
  };

  const handleOnAction = () => {
    reset();
    setIsTimedOut(false);
  };

  const resetIdleTimerHandler = () => {
    reset();
    setIsTimedOut(false);
    setShowModal(false);
    setCounter(30);
  };

  const { reset } = useIdleTimer({
    timeout: 1000 * 60 * 15,
    onIdle: handleOnIdle,
    onAction: handleOnAction,
    debounce: 500,
  });

  const snackbarRef = useRef<{ closeToast: () => void }>(null);

  return (
    <div className='h-screen flex overflow-hidden bg-white'>
      {showModal && (
        <Modal>
          <h2 className='m-0'>Inactvitiy Auto Logout</h2>
          <p className='py-2 pb-5'>
            Are you still with us ? For security reason, You would be automatically logged out in{' '}
            <strong className='text-tertiaryColor-normal'>{counter}s</strong>
          </p>
          <div className='flex gap-4'>
            <Button label="I'm still here" btnStyle='btn-primary' btnType='button' onClick={resetIdleTimerHandler} />
            <Button label='Signout' btnStyle='btn-neutral' btnType='button' onClick={() => dispatch(SignOutUser())} />
          </div>
        </Modal>
      )}
      <SidebarNav sidebarOpen={sidebarOpen} closeSideBar={() => setSidebarOpen(false)} />
      {/* header menu button on Small Screen becomes part of the Header Bar*/}
      <div className='flex flex-col min-w-0 flex-1 overflow-hidden'>
        <div className='lg:hidden'>
          <div className='flex items-center justify-between bg-gray-50 border-b border-gray-200 px-4 py-1.5'>
            <button
              type='button'
              className='-mr-3 h-12 w-12 inline-flex items-center justify-center rounded-md text-gray-500 hover:text-gray-900'
              onClick={() => setSidebarOpen(true)}
            >
              <span className='sr-only'>Open sidebar</span>
              <Bars3BottomLeftIcon className='h-6 w-6' aria-hidden='true' />
            </button>
            <div className='flex-grow flex justify-center'>
              <img
                className='h-8 w-auto'
                src='https://assets.seinterminal.com/images/logos/SEIN_Tech_Partners_Logo_Color.png'
                alt='sein technology partners logo'
              />
            </div>
          </div>
        </div>
        <div className='flex-1 flex flex-col overflow-hidden'>
          <HeaderBar />
          <Snackbar ref={snackbarRef} />
          <div className='flex-1 flex items-stretch overflow-hidden'>
            <main className='flex-1 overflow-y-auto p-10 rounded-md'>
              <div className='mb-5'>
                <Breadcrumbs />
              </div>
              <Outlet />
            </main>
          </div>
        </div>
      </div>
    </div>
  );
}
